import React, { useState } from 'react'
import { tileModels, type TileVariant } from '../utils/tileTextures'

interface Tile {
  id: string
  name: string
  texturePath: string
  tileSize: number
}

// Helper do kodowania ścieżek tekstur (koduje spacje i specjalne znaki)
function encodeTexturePath(path: string): string {
  // Rozdziel ścieżkę na części i zakoduj każdą część osobno
  const parts = path.split('/')
  return parts.map(part => {
    // Zakoduj tylko część z nazwą pliku/folderu, nie znaki specjalne ścieżki
    if (part && part !== '') {
      return encodeURIComponent(part)
    }
    return part
  }).join('/')
}

interface TileSelectionPopupProps {
  isOpen: boolean
  onClose: () => void
  onTileSelect: (tile: Tile) => void
  selectedTile?: Tile
}

const TileSelectionPopup: React.FC<TileSelectionPopupProps> = ({ 
  isOpen, 
  onClose, 
  onTileSelect, 
  selectedTile 
}) => {
  const [step, setStep] = useState<'model' | 'color' | 'variant'>('model')
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null)
  const [selectedColorId, setSelectedColorId] = useState<string | null>(null)

  const handleModelSelect = (modelId: string) => {
    setSelectedModelId(modelId)
    setStep('color')
  }

  const handleColorSelect = (colorId: string) => {
    setSelectedColorId(colorId)
    setStep('variant')
  }

  const handleVariantSelect = (variant: TileVariant) => {
    // Konwertuj TileVariant na Tile
    const tile: Tile = {
      id: variant.id,
      name: `${tileModels.find(m => m.id === selectedModelId)?.name} - ${tileModels.find(m => m.id === selectedModelId)?.colors.find(c => c.id === selectedColorId)?.name} - ${variant.name}`,
      texturePath: variant.texturePath,
      tileSize: 70
    }
    onTileSelect(tile)
    onClose()
    // Resetuj stan po zamknięciu
    setStep('model')
    setSelectedModelId(null)
    setSelectedColorId(null)
  }

  const handleBack = () => {
    if (step === 'variant') {
      setStep('color')
      setSelectedColorId(null)
    } else if (step === 'color') {
      setStep('model')
      setSelectedModelId(null)
    }
  }

  const handleClose = () => {
    onClose()
    // Resetuj stan po zamknięciu
    setStep('model')
    setSelectedModelId(null)
    setSelectedColorId(null)
  }

  const selectedModel = selectedModelId ? tileModels.find(m => m.id === selectedModelId) : null
  const selectedColor = selectedModel && selectedColorId ? selectedModel.colors.find(c => c.id === selectedColorId) : null

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div>
            <h2 className="font-sans text-2xl font-bold text-gray-900 mb-1">
              Wybierz Płytkę
            </h2>
            <p className="font-sans text-sm text-gray-600">
              Wybierz idealną płytkę dla swojej powierzchni
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-8 py-6 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 ${step === 'model' ? 'text-red-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step === 'model' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  1
                </div>
                <span className="font-sans font-medium text-sm">Model</span>
              </div>
              <div className={`w-8 h-0.5 ${step !== 'model' ? 'bg-red-600' : 'bg-gray-300'}`}></div>
              <div className={`flex items-center space-x-2 ${step === 'color' ? 'text-red-600' : step === 'variant' ? 'text-gray-400' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step === 'color' ? 'bg-red-600 text-white' : step === 'variant' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  2
                </div>
                <span className="font-sans font-medium text-sm">Kolor</span>
              </div>
              <div className={`w-8 h-0.5 ${step === 'variant' ? 'bg-red-600' : 'bg-gray-300'}`}></div>
              <div className={`flex items-center space-x-2 ${step === 'variant' ? 'text-red-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step === 'variant' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  3
                </div>
                <span className="font-sans font-medium text-sm">Wariant</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[55vh]">
          {/* Step 1: Model */}
          {step === 'model' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="w-20"></div>
                <div className="text-center">
                  <h3 className="font-sans text-2xl font-bold text-gray-900 mb-2">Wybierz model kostki</h3>
                  <p className="font-sans text-gray-600">Wybierz model kostki brukowej</p>
                </div>
                <div className="w-20"></div>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {tileModels.map(model => (
                  <button
                    key={model.id}
                    onClick={() => handleModelSelect(model.id)}
                    className="group p-6 border-2 border-gray-200 rounded-xl hover:border-red-400 hover:bg-red-50 hover:shadow-lg transition-all text-center transform hover:scale-105"
                  >
                    <div className="w-12 h-12 bg-gray-100 group-hover:bg-red-100 rounded-full mx-auto mb-3 flex items-center justify-center">
                      <span className="text-xl">🧱</span>
                    </div>
                    <span className="font-sans font-semibold text-gray-900 group-hover:text-red-700">{model.name}</span>
                    <div className="text-xs text-gray-500 mt-1">{model.colors.length} kolorów</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Kolor */}
          {step === 'color' && selectedModel && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="font-sans font-medium">Wstecz</span>
                </button>
                <div className="text-center">
                  <h3 className="font-sans text-2xl font-bold text-gray-900 mb-1">Wybierz kolor</h3>
                  <p className="font-sans text-gray-600">
                    Model: <span className="font-semibold text-red-600">{selectedModel.name}</span>
                  </p>
                </div>
                <div className="w-20"></div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {selectedModel.colors.map(color => (
                  <button
                    key={color.id}
                    onClick={() => handleColorSelect(color.id)}
                    className="group p-6 border-2 border-gray-200 rounded-xl hover:border-red-400 hover:bg-red-50 hover:shadow-lg transition-all text-left transform hover:scale-[1.02]"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-sans font-semibold text-lg text-gray-900 group-hover:text-red-700">{color.name}</h4>
                      <span className="px-3 py-1 bg-gray-100 group-hover:bg-red-100 text-gray-600 group-hover:text-red-700 text-xs font-medium rounded-full">
                        {color.variants.length} wariant{color.variants.length !== 1 ? 'ów' : ''}
                      </span>
                    </div>
                    {color.variants.length > 0 && (
                      <div className="flex justify-between items-center pt-3 border-t border-gray-100 group-hover:border-red-100">
                        <div className="flex items-center space-x-2">
                          <img
                            src={encodeTexturePath(color.variants[0].texturePath)}
                            alt={color.name}
                            className="w-16 h-16 bg-gray-100 group-hover:bg-red-100 rounded border-2 border-gray-300 group-hover:border-red-400 transition-all object-cover"
                            loading="lazy"
                            onError={(e) => {
                              // Fallback przy błędzie ładowania
                              const target = e.currentTarget as HTMLImageElement
                              console.error('Błąd ładowania tekstury:', color.variants[0].texturePath)
                              target.style.backgroundColor = '#e5e7eb'
                              target.style.border = '2px solid #d1d5db'
                              target.src = '/textures/kostka_bruk.png' // Fallback
                            }}
                          />
                          <span className="font-sans text-xs text-gray-500">
                            Podgląd
                          </span>
                        </div>
                        <span className="font-sans font-bold text-red-600 text-lg">
                          Wybierz
                        </span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Wariant */}
          {step === 'variant' && selectedColor && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="font-sans font-medium">Wstecz</span>
                </button>
                <div className="text-center">
                  <h3 className="font-sans text-2xl font-bold text-gray-900 mb-1">Wybierz wariant</h3>
                  <p className="font-sans text-gray-600">
                    {selectedModel?.name} - <span className="font-semibold text-red-600">{selectedColor.name}</span>
                  </p>
                </div>
                <div className="w-20"></div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {selectedColor.variants.map(variant => (
                  <button
                    key={variant.id}
                    onClick={() => handleVariantSelect(variant)}
                    className="group p-6 border-2 border-gray-200 rounded-xl hover:border-red-400 hover:bg-red-50 hover:shadow-lg transition-all text-left transform hover:scale-[1.02]"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-sans font-semibold text-lg text-gray-900 group-hover:text-red-700">{variant.name}</h4>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-gray-100 group-hover:border-red-100">
                      <div className="flex items-center space-x-2">
                        <img
                          src={encodeTexturePath(variant.texturePath)}
                          alt={variant.name}
                          className="w-24 h-24 bg-gray-100 group-hover:bg-red-100 rounded border-2 border-gray-300 group-hover:border-red-400 transition-all object-cover"
                          loading="lazy"
                          onError={(e) => {
                            // Fallback przy błędzie ładowania
                            const target = e.currentTarget as HTMLImageElement
                            console.error('Błąd ładowania tekstury:', variant.texturePath)
                            target.style.backgroundColor = '#e5e7eb'
                            target.style.border = '2px solid #d1d5db'
                            target.src = '/textures/kostka_bruk.png' // Fallback
                          }}
                        />
                        <span className="font-sans text-xs text-gray-500">
                          Podgląd tekstury
                        </span>
                      </div>
                      <span className="font-sans font-bold text-red-600 text-lg">
                        Wybierz
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 sm:p-6 md:p-8 border-t border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          {/* Mobile: Teksty na górze */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 order-1 sm:order-1">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></div>
                <p className="font-sans text-base sm:text-base text-gray-600">
                  Krok {step === 'model' ? 1 : step === 'color' ? 2 : 3} z 3
                </p>
              </div>
              {step !== 'model' && (
                <span className="font-sans text-sm sm:text-sm text-gray-500">
                  Możesz wrócić i zmienić swój wybór
                </span>
              )}
            </div>
            {/* Mobile: Przyciski na dole, Desktop: na prawo */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-3 w-full sm:w-auto order-2 sm:order-2">
              {step !== 'model' && (
                <button
                  onClick={() => {
                    setStep('model')
                    setSelectedModelId(null)
                    setSelectedColorId(null)
                  }}
                  className="w-full sm:w-auto px-5 py-3 sm:py-2 text-base sm:text-base text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all font-sans font-medium touch-manipulation mobile-button"
                >
                  Zacznij od nowa
                </button>
              )}
              <button
                onClick={handleClose}
                className="w-full sm:w-auto px-5 sm:px-6 py-3 sm:py-2 text-base sm:text-base text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all font-sans font-medium touch-manipulation mobile-button"
              >
                Anuluj
              </button>
              {step === 'variant' && (
                <span className="w-full sm:w-auto px-5 py-3 sm:py-2 bg-green-100 text-green-700 rounded-lg font-sans text-base sm:text-base font-medium text-center">
                  Wybierz wariant aby zakończyć
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TileSelectionPopup

